import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState, type DragEvent, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  OrgTreeSelector,
  useAlert,
  useCompanyFeature,
  useCurrentCompany,
} from '@employ-engage/common-components';
import { CompanyFeaturesEnum } from '@employ-engage/data';

export interface AddEmployeesBulkFormProps {
  setOpen: (open: boolean) => void;
  setCurrentTab: (currentTab: 'bulk' | 'single') => void;
  refechEmployeeTable: () => void;
}

export const AddEmployeesBulkForm = ({
  setOpen,
  setCurrentTab,
  refechEmployeeTable,
}: AddEmployeesBulkFormProps) => {
  const { t } = useTranslation(['common']);
  const [
    exampleBulkEmployeeUploadFileUrl,
    setExampleBulkEmployeeUploadFileUrl,
  ] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const { currentCompany, refetchCompany } = useCurrentCompany();
  const { feature: multiplePhoneNumbers } = useCompanyFeature(
    CompanyFeaturesEnum.MULTIPLE_PHONE_NUMBERS
  );
  const {
    Alert: SuccessfulCopyAlert,
    fireAlert: fireSuccessfulCopyAlert,
    setAlertMessage: setfireSuccessfulCopyAlertMessage,
  } = useAlert({
    title: t('Copied to clipboard'),
    message: t('The department has been copied to your clipboard.'),
    type: 'success',
  });
  const {
    Alert: FailedToCopyAlert,
    fireAlert: fireFailedToCopyAlert,
    setAlertMessage: setfireFailedToCopyAlertMessage,
  } = useAlert({
    title: t('Failed to copy to clipboard'),
    message: t('The department could not be copied to your clipboard.'),
    type: 'error',
  });

  useEffect(() => {
    fetch(
      import.meta.env.VITE_API_ENDPOINT +
        '/protected-api/assets/example-bulk-employee-upload.csv'
    )
      .then((response) => {
        if (response.ok) {
          setExampleBulkEmployeeUploadFileUrl(response.url);
        } else {
          console.error('Failed to fetch example bulk employee upload file');
        }
      })
      .catch((error) => {
        console.error(
          'Error fetching example bulk employee upload file:',
          error
        );
      });
  }, []);

  const { handleSubmit, setFieldValue, values } = useFormik({
    initialValues: {
      fileUpload: null,
    },
    onSubmit: (values) => {
      if (values?.fileUpload === null) return;

      fetch(
        import.meta.env.VITE_API_ENDPOINT + '/protected-api/upload-employees',
        {
          method: 'POST',
          body: (() => {
            const formData = new FormData();
            formData.append('file', values.fileUpload as Blob);
            return formData;
          })(),
          credentials: 'include',
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          setOpen(false);
          setCurrentTab('single');
        })
        .catch((error) => {
          console.error('Error uploading file:', error);
        })
        .finally(() => {
          refetchCompany();
          refechEmployeeTable?.();
        });
    },
    validationSchema: Yup.object({
      fileUpload: Yup.mixed().required(t('Required')),
    }),
  });

  const handleFileUpload = (e: any) => {
    setFieldValue('fileUpload', e.target.files[0]);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items && e.dataTransfer.items[0]) {
      const file = e.dataTransfer.items[0].getAsFile();
      setFieldValue('fileUpload', file);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className="flex justify-between">
      <form onSubmit={handleSubmit} className="flex flex-col justify-between">
        <div>
          {exampleBulkEmployeeUploadFileUrl && (
            <div className="text-base font-semibold font-['Poppins'] leading-normal">
              <a
                className="text-blue-500"
                href={exampleBulkEmployeeUploadFileUrl}
              >
                {t('Download the template')}
              </a>
            </div>
          )}
          {multiplePhoneNumbers?.isEnabled && (
            <p className="mt-2 mb-3 w-96 text-sm text-gray-600">
              {t(
                'Put several numbers in the phoneNumber column with a semicolon between them. Imported numbers get no texts until each employee confirms them; we email them a link.'
              )}
            </p>
          )}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className={classNames(
              'w-96 h-64 px-8 rounded-lg border-2 flex-col justify-center items-center flex',
              {
                'border-dotted': !values.fileUpload,
                'border-solid': values.fileUpload,
                'border-zinc-300': !isDragging,
                'border-light-secondaryAccent': isDragging,
              }
            )}
          >
            <div className="self-stretch text-center">
              {values.fileUpload ? (
                <span className="text-neutral-600 text-sm font-normal font-['Poppins']">
                  {(values.fileUpload as File)?.name}
                </span>
              ) : (
                <>
                  <label
                    htmlFor="fileUpload"
                    className="text-green-500 text-sm font-normal font-['Poppins'] cursor-pointer"
                  >
                    {t('browse files')}
                  </label>
                  <input
                    className="hidden"
                    type="file"
                    accept=".csv"
                    id="fileUpload"
                    name="fileUpload"
                    onChange={handleFileUpload}
                  />
                  <span className="text-neutral-600 text-sm font-normal font-['Poppins']">
                    {' '}
                    {t('or drag and drop your files here')}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="self-stretch mt-5 justify-start items-start grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="col-span-1 h-12 px-4 py-3 bg-white rounded-lg shadow border border-gray-300 justify-center items-center text-zinc-800 text-base font-semibold font-['Poppins'] leading-normal"
          >
            {t('Cancel')}
          </button>
          <button
            disabled={!values.fileUpload}
            type="submit"
            className={classNames(
              "col-span-2 h-12 px-4 py-3 rounded-lg shadow border justify-center items-center text-white text-base font-semibold font-['Poppins'] leading-normal",
              {
                'bg-dark-primaryBackground border-dark-primaryBackground':
                  values.fileUpload,
                'border-gray-400 bg-gray-400 cursor-not-allowed':
                  !values.fileUpload,
              }
            )}
          >
            {t('Upload')}
          </button>
        </div>
      </form>
      <div className="flex justify-center flex-grow">
        <div className="flex flex-col space-y-4 items-center">
          <div className="text-bold">
            {t('Select an organization to copy company path to the clipboard')}
          </div>
          <OrgTreeSelector
            onSelectOrganization={(targetDepartment) => {
              const textToCopy = targetDepartment.replace(/\./g, '~');
              navigator.clipboard
                .writeText(textToCopy)
                .then(() => {
                  setfireSuccessfulCopyAlertMessage(
                    '"' + textToCopy + '" has been copied to your clipboard.'
                  );
                  fireSuccessfulCopyAlert(3000);
                })
                .catch((err) => {
                  setfireFailedToCopyAlertMessage(
                    '"' +
                      textToCopy +
                      '" could not be copied to your clipboard.'
                  );
                  fireFailedToCopyAlert(3000);
                });
            }}
            companyData={currentCompany}
            refetchCompany={refetchCompany}
          />
        </div>
      </div>
      <SuccessfulCopyAlert />
      <FailedToCopyAlert />
    </div>
  );
};

export default AddEmployeesBulkForm;
